import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import OtpInput from 'react-otp-input';
import { signUp, confirmSignUp, signIn } from 'aws-amplify/auth';
import { registerUser, checkAuthState } from '../store/authSlice';
import { resendVerificationCode } from '../util/resendSignUpCode';

import classes from "./SignIn.module.css";
import Modal from "./UI/Modal";
import SocialForm from './UI/SocialForm';
import GermanFlag from "../public/HomepagePhotos/germanFlag.svg";
import closeButton from "../public/icons/Schließen.svg";
import backArrow from "../public/icons/ZurückIcon.svg";
import { mapCognitoErrorToGerman } from '../util/authErrorMessages';
import { toApiBirthDate } from '../util/birthDateUtils';
import { getAuthRedirectPath, normalizeRedirectPath } from '../util/redirectUtils';
import { trackSignup } from '../util/tracking';

const OtpChecker = ({
  telephone,
  email,
  onOtpSubmit,
  onUberDichDisplay,
  isRetryAttempt = false,
  isVerifying = false,
  submitError = '',
  onClearSubmitError = () => {}
}) => {
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [isResending, setIsResending] = useState(false);
  const containerRef = useRef(null);
  const displayedError = otpError || submitError;

  useEffect(() => {
    // Auto-focus the first input when component mounts
    const timer = setTimeout(() => {
      const firstInput = containerRef.current?.querySelector('input');
      if (firstInput) {
        firstInput.focus();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!otp) {
      setOtpError('Code muss ausgefüllt werden');
      return;
    }

    try {
      // Confirm signup with Cognito
      await confirmSignUp({
        username: email || telephone,
        confirmationCode: otp
      });

      setOtpError('');
      await onOtpSubmit();
      onUberDichDisplay();
    } catch (error) {
      const germanMessage = mapCognitoErrorToGerman(error);
      setOtpError(germanMessage);
    }
  };

  const handleResendCode = async () => {
    if (isResending) return;

    setIsResending(true);
    const username = email || `+49${telephone}`;

    try {
      const result = await resendVerificationCode(username);

      if (result.success) {
        // You can show a success message or just clear any existing errors
        setOtpError('');
        // Optionally, you can show a success message for a few seconds
        // setSuccessMessage('Code wurde erneut gesendet');
      } else {
        setOtpError(result.message);
      }
    } catch (error) {
      const germanMessage = mapCognitoErrorToGerman(error);
      setOtpError(germanMessage);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className={classes.telephoneContainer}>
      <div className={classes.telephoneInfosContainer}>
        <span className='regularLarge'>
          {isRetryAttempt
            ? `Du hast bereits ein Konto mit ${email || `+49 ${telephone}`}. Gib den Bestätigungscode ein, um die Registrierung abzuschließen.`
            : `Gib den 6-stelligen Code ein, den wir an ${email || `+49 ${telephone}`} gesendet haben.`
          }
        </span>
      </div>
      <div className={classes.spamContainer}>
        <span className='regularSmall'>📩 Auch im SPAM-Ordner nachschauen!</span>
      </div>
      <div className={classes.otpInput} style={{ marginBottom: displayedError ? '15px' : '30px' }} ref={containerRef}>
        <OtpInput
          value={otp}
          onChange={(otp) => { setOtp(otp); setOtpError(''); onClearSubmitError(); }}
          numInputs={6}
          renderInput={(props) => {
            const handleKeyDown = (e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit(e);
                return;
              }
              if (e.key === 'Backspace' && e.currentTarget.value) {
                // When deleting a character, stop propagation to prevent focus change
                e.stopPropagation();
              }
              // Call original onKeyDown if it exists
              if (props.onKeyDown) {
                props.onKeyDown(e);
              }
            };
            return <input {...props} inputMode="numeric" onKeyDown={handleKeyDown} />;
          }}
          shouldAutoFocus
        />
      </div>
      {displayedError &&
        <div className={classes.errorContainer}>
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M1.02734 1.40723L10.9725 11.3524" stroke="#CB1541" strokeWidth="1.5" strokeMiterlimit="10"/>
            <path fillRule="evenodd" clipRule="evenodd" d="M4.9452 6.37988L0.5 1.93469L1.5548 0.879883L6 5.32508L10.4452 0.879883L11.5 1.93469L7.0548 6.37988L11.5 10.8251L10.4452 11.8799L6 7.43469L1.5548 11.8799L0.5 10.8251L4.9452 6.37988Z" fill="#CB1541"/>
          </svg>
          <span className='slimRegularSmallText'>{displayedError}</span>
        </div>
      }
      <button
        className={classes.buttonWeiter}
        type="submit"
        onClick={handleSubmit}
        disabled={isVerifying}
        style={{ opacity: 1, cursor: isVerifying ? 'not-allowed' : 'pointer' }}
      >
        {isVerifying ? 'Wird überprüft...' : 'Überprüfen'}
      </button>
      <div className={classes.keinSmsComponent}>
        <span>Keine E-Mail erhalten?</span>
        <button
          className={classes.underlineSpan}
          onClick={handleResendCode}
          disabled={isResending}
        >
          {isResending ? 'Wird gesendet...' : 'Erneut senden'}
        </button>
      </div>
    </div>
  );
};

export const UberDich = ({
  email,
  telephone,
  password,
  onUberDichSubmit,
  isCreating = false,
  submitLabel = 'Weiter',
  loadingLabel = 'Wird erstellt...'
}) => {
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [birthday, setBirthday] = useState('');
  const [isNameFocused, setIsNameFocused] = useState(false);
  const [isSurnameFocused, setIsSurnameFocused] = useState(false);
  const [isBirthdayFocused, setIsBirthdayFocused] = useState(false);
  const [nameError, setNameError] = useState('');
  const [surnameError, setSurnameError] = useState('');
  const [birthdayError, setBirthdayError] = useState('');

  const handleFocus = (setFocus) => {
    setFocus(true);
  };

  const handleBlur = (e, setFocus) => {
    if (!e.target.value) {
      setFocus(false);
    }
  };

  const handleNameChange = (e, setNameFunc) => {
    const value = e.target.value;
    // Only allow letters, spaces, hyphens, and German umlauts
    const filteredValue = value.replace(/[^a-zA-ZäöüÄÖÜß\s-]/g, '');
    setNameFunc(filteredValue);
  };

  const calculateAge = (birthdate) => {
    const [day, month, year] = birthdate.split('.').map(Number);
    const birthDate = new Date(year, month - 1, day);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  };

  const handleBirthdayChange = (e) => {
    const value = e.target.value;

    // Remove all non-numeric characters except dots
    let cleanValue = value.replace(/[^\d.]/g, '');

    // Remove extra dots (only allow maximum 2 dots)
    const dotCount = (cleanValue.match(/\./g) || []).length;
    if (dotCount > 2) {
      cleanValue = cleanValue.replace(/\.(?=.*\..*\.)/, '');
    }

    // Auto-format as DD.MM.YYYY
    let formattedValue = '';
    const numbers = cleanValue.replace(/\./g, '');

    for (let i = 0; i < numbers.length && i < 8; i++) {
      if (i === 2 || i === 4) {
        formattedValue += '.';
      }
      formattedValue += numbers[i];
    }

    // Validate day (01-31)
    if (formattedValue.length >= 2) {
      const day = parseInt(formattedValue.substring(0, 2));
      if (day < 1 || day > 31) {
        return; // Don't update if day is invalid
      }
    }

    // Validate month (01-12)
    if (formattedValue.length >= 5) {
      const month = parseInt(formattedValue.substring(3, 5));
      if (month < 1 || month > 12) {
        return; // Don't update if month is invalid
      }
    }

    // Validate year (must be 4 digits and reasonable range)
    if (formattedValue.length === 10) {
      const year = parseInt(formattedValue.substring(6, 10));
      const currentYear = new Date().getFullYear();
      if (year < 1900 || year > currentYear) {
        return; // Don't update if year is invalid
      }
    }

    setBirthday(formattedValue);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear all previous errors
    setNameError('');
    setSurnameError('');
    setBirthdayError('');

    let hasErrors = false;

    // Validation: Check if all fields are filled
    if (!name.trim()) {
      setNameError('Vorname muss ausgefüllt werden.');
      hasErrors = true;
    }

    if (!surname.trim()) {
      setSurnameError('Nachname muss ausgefüllt werden.');
      hasErrors = true;
    }

    if (!birthday || birthday.length !== 10) {
      setBirthdayError('Geburtsdatum muss vollständig ausgefüllt werden.');
      hasErrors = true;
    }

    // Validation: Check that names don't contain numbers
    const nameRegex = /^[a-zA-ZäöüÄÖÜß\s-]+$/;
    if (name.trim() && !nameRegex.test(name)) {
      setNameError('Vorname darf keine Zahlen oder Sonderzeichen enthalten.');
      hasErrors = true;
    }

    if (surname.trim() && !nameRegex.test(surname)) {
      setSurnameError('Nachname darf keine Zahlen oder Sonderzeichen enthalten.');
      hasErrors = true;
    }

    // Validation: Check age
    if (birthday && birthday.length === 10) {
      const age = calculateAge(birthday);
      if (age < 18) {
        setBirthdayError('Du musst mindestens 18 Jahre alt sein.');
        hasErrors = true;
      }
    }

    // If there are any errors, stop submission
    if (hasErrors) {
      return;
    }

    // Pass the validated data back to parent component
    // Parent will handle loading state
    await onUberDichSubmit({
      name,
      surname,
      birthday
    });
  };

  return (
    <form className={classes.formUberDich} onSubmit={handleSubmit}>
      <div className={classes.uberDichInputContainer}>
        <div className={classes.nameContainer}>
          <label className={`${classes.label} ${isNameFocused || name ? classes.focused : ''}`}>
            Vorname
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => handleNameChange(e, setName)}
            onFocus={() => handleFocus(setIsNameFocused)}
            onBlur={(e) => handleBlur(e, setIsNameFocused)}
          />
          {nameError && (
            <div className={classes.uberDichError} style={{ marginTop: '10px' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M1.02734 1.40723L10.9725 11.3524" stroke="#CB1541" strokeWidth="1.5" strokeMiterlimit="10" />
                <path fillRule="evenodd" clipRule="evenodd" d="M4.9452 6.37988L0.5 1.93469L1.5548 0.879883L6 5.32508L10.4452 0.879883L11.5 1.93469L7.0548 6.37988L11.5 10.8251L10.4452 11.8799L6 7.43469L1.5548 11.8799L0.5 10.8251L4.9452 6.37988Z" fill="#CB1541" />
              </svg>
              <span>{nameError}</span>
            </div>
          )}
        </div>
        <div className={classes.nameContainer}>
          <label className={`${classes.label} ${isSurnameFocused || surname ? classes.focused : ''}`}>
            Nachname
          </label>
          <input
            type="text"
            value={surname}
            onChange={(e) => handleNameChange(e, setSurname)}
            onFocus={() => handleFocus(setIsSurnameFocused)}
            onBlur={(e) => handleBlur(e, setIsSurnameFocused)}
          />
          {surnameError && (
            <div className={classes.uberDichError} style={{ marginTop: '10px' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M1.02734 1.40723L10.9725 11.3524" stroke="#CB1541" strokeWidth="1.5" strokeMiterlimit="10" />
                <path fillRule="evenodd" clipRule="evenodd" d="M4.9452 6.37988L0.5 1.93469L1.5548 0.879883L6 5.32508L10.4452 0.879883L11.5 1.93469L7.0548 6.37988L11.5 10.8251L10.4452 11.8799L6 7.43469L1.5548 11.8799L0.5 10.8251L4.9452 6.37988Z" fill="#CB1541" />
              </svg>
              <span>{surnameError}</span>
            </div>
          )}
        </div>
      </div>
      {!nameError && !surnameError && (
        <span className={`${classes.nameInfosContainer} lightSmall`}>
          Dein Name muss mit deinem Ausweisdokument übereinstimmen. Dein Nachname wird anderen Nutzern nicht gezeigt.
        </span>
      )}
      <div className={classes.birthdayContainer}>
        <label className={`${classes.label} ${isBirthdayFocused || birthday ? classes.focused : ''}`}>
          Geburtsdatum (TT.MM.JJJJ)
        </label>
        <input
          type="text"
          value={birthday}
          onChange={handleBirthdayChange}
          onFocus={() => handleFocus(setIsBirthdayFocused)}
          onBlur={(e) => handleBlur(e, setIsBirthdayFocused)}
          placeholder={isBirthdayFocused ? "TT.MM.JJJJ" : ""}
          maxLength="10"
          inputMode="numeric"
        />
      </div>
      {!birthdayError && (
        <span className={`${classes.nameInfosContainer} lightSmall`}>
          Du musst mindestens 18 Jahre alt sein. Dein Alter wird anderen Nutzern nicht gezeigt.
        </span>
      )}
      {birthdayError && (
        <div style={{ marginBottom: '20px' }} className={classes.uberDichError}>
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M1.02734 1.40723L10.9725 11.3524" stroke="#CB1541" strokeWidth="1.5" strokeMiterlimit="10" />
            <path fillRule="evenodd" clipRule="evenodd" d="M4.9452 6.37988L0.5 1.93469L1.5548 0.879883L6 5.32508L10.4452 0.879883L11.5 1.93469L7.0548 6.37988L11.5 10.8251L10.4452 11.8799L6 7.43469L1.5548 11.8799L0.5 10.8251L4.9452 6.37988Z" fill="#CB1541" />
          </svg>
          <span>{birthdayError}</span>
        </div>
      )}
      <button
        className={classes.buttonSubmit}
        type="submit"
        disabled={isCreating}
        style={{ opacity: 1, cursor: isCreating ? 'not-allowed' : 'pointer' }}
      >
        {isCreating ? loadingLabel : submitLabel}
      </button>
      <span className={`${classes.detailsSavedText} slimRegularMiniText`}>
        Indem du fortfährst, stimmst du den <Link style={{ color: 'black !important', textDecoration: 'underline' }} to="/agb">AGB</Link> von Stu zu und bestätigst, dass du unsere <Link style={{ color: 'black !important', textDecoration: 'underline' }} to="/datenschutz">Datenschutzrichtlinien</Link> gelesen hast.
      </span>
    </form>
  );
};

export const Register = ({ open, onClose, onSwitchToSignIn }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [telephone, setTelephone] = useState('');
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [birthday, setBirthday] = useState('');
  const [errors, setErrors] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [showTelephoneInput, setShowTelephoneInput] = useState(false);
  const [uberDichSubmitted, setUberDichSubmitted] = useState(false);
  const [telephoneSubmitted, setTelephoneSubmitted] = useState(false);
  const [otpSubmitted, setOtpSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [resetSocialForm, setResetSocialForm] = useState(false);
  const [isUberDichDisplayed, setIsUberDichDisplayed] = useState(false);
  const [isRetryAttempt, setIsRetryAttempt] = useState(false);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpSubmitError, setOtpSubmitError] = useState('');

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = (e) => {
    if (!e.target.value) {
      setIsFocused(false);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) {
      errors.push('Mindestens 8 Zeichen lang.');
    }
    if (!/\d/.test(password)) {
      errors.push('Mindestens eine Zahl.');
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]/.test(password)) {
      errors.push('Mindestens ein Sonderzeichen.');
    }
    return errors;
  };

  const handlePasswordFocus = () => {
    setIsFocused(true);
    setPasswordTouched(true);
    updatePasswordErrors(password);
  };

  const handlePasswordBlur = (e) => {
    if (!e.target.value) {
      setIsFocused(false);
    }
    updatePasswordErrors(e.target.value);
  };

  const updatePasswordErrors = (password) => {
    if (passwordTouched) {
      const passwordErrors = validatePassword(password);
      setErrors(passwordErrors);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let newErrors = [];

    if (showTelephoneInput) {
      if (!telephone) {
        newErrors.push('Telefonnummer muss ausgefüllt werden');
      } else {
        // Just move to Über dich form, don't create user yet
        setErrors([]);
        setIsUberDichDisplayed(true);
      }
    } else {
      if (!emailSubmitted) {
        if (!email) {
          newErrors.push('E-Mail Adresse muss ausgefüllt werden');
        } else if (!validateEmail(email)) {
          newErrors.push('E-Mail Adresse ist nicht gültig');
        } else {
          setEmailSubmitted(true);
          updatePasswordErrors('');
          return;
        }
      } else {
        if (!password) {
          newErrors.push('Passwort muss ausgefüllt werden');
        } else {
          const validationErrors = validatePassword(password);
          if (validationErrors.length === 0) {
            // Check if user already exists by attempting to sign in
            try {
              const { isSignedIn, nextStep } = await signIn({
                username: email,
                password: password
              });

              if (nextStep.signInStep === 'CONFIRM_SIGN_UP') {
                // User exists but not verified - this is the retry case
                // Collect profile data before OTP so the backend update cannot be empty.
                setIsRetryAttempt(true);
                setErrors([]);
                setIsUberDichDisplayed(true);
                setUberDichSubmitted(false);
                setTelephoneSubmitted(false);
                return;
              } else if (isSignedIn) {
                // User is already verified and signed in - update Redux state
                await dispatch(checkAuthState());
                onClose();
                return;
              }
            } catch (signInError) {
              // User doesn't exist or wrong password - check error type
              if (signInError.name === 'UserNotFoundException' || signInError.message.includes('User does not exist')) {
                // Good! User doesn't exist, proceed to Über dich
                setErrors([]);
                setIsUberDichDisplayed(true);
                return;
              } else if (signInError.name === 'NotAuthorizedException') {
                // Email exists but wrong password
                newErrors.push('Diese E-Mail ist bereits registriert. Bitte melde dich an oder setze dein Passwort zurück.');
              } else {
                // Other errors - still proceed but log the error
                setErrors([]);
                setIsUberDichDisplayed(true);
                return;
              }
            }
          } else {
            newErrors = [...validationErrors];
          }
        }
      }
    }
    setErrors(newErrors);
  };

  const handleUberDichSubmit = async (userData) => {
    // Store the user data
    setName(userData.name);
    setSurname(userData.surname);
    setBirthday(userData.birthday);
    setIsCreatingAccount(true); // Start loading

    if (isRetryAttempt) {
      setErrors([]);
      setUberDichSubmitted(true);
      setIsCreatingAccount(false);
      setTelephoneSubmitted(true); // Move to OTP screen
      return;
    }

    try {
      // Create the user with ALL attributes
      let signUpResult;
      if (showTelephoneInput) {
        signUpResult = await signUp({
          username: `+49${telephone}`,
          password: Math.random().toString(36).slice(-8),
          attributes: {
            phone_number: `+49${telephone}`,
            given_name: userData.name,
            family_name: userData.surname,
            birthdate: toApiBirthDate(userData.birthday)
          }
        });
      } else {
        signUpResult = await signUp({
          username: email,
          password: password,
          attributes: {
            email: email,
            given_name: userData.name,
            family_name: userData.surname,
            birthdate: toApiBirthDate(userData.birthday)
          }
        });
      }

      setErrors([]);
      setUberDichSubmitted(true);
      setIsCreatingAccount(false); // End loading
      setTelephoneSubmitted(true); // Move to OTP screen
    } catch (error) {
      setIsCreatingAccount(false); // End loading on error

      // Smart Error Handling
      if (error.name === 'UsernameExistsException' || error.message.includes('User already exists')) {
        // User exists but might not be verified
        try {
          const { isSignedIn, nextStep } = await signIn({
            username: email || `+49${telephone}`,
            password: password || Math.random().toString(36).slice(-8)
          });

          if (nextStep.signInStep === 'CONFIRM_SIGN_UP') {
            // User exists but not verified
            setErrors(['Du hast deine E-Mail noch nicht bestätigt. Unbedingt im Spam-Ordner nach dem Code schauen.']);
            setIsRetryAttempt(true);
            setUberDichSubmitted(true);
            setTelephoneSubmitted(true); // Move to OTP screen
          } else if (isSignedIn) {
            // User is already verified and signed in - update Redux state
            await dispatch(checkAuthState());
            onClose();
          }
        } catch (signInError) {
          if (signInError.name === 'NotAuthorizedException') {
            setErrors(['E-Mail bereits registriert. Bitte verwende "Anmelden" oder setze dein Passwort zurück.']);
          } else {
            setErrors(['Du hast deine E-Mail noch nicht bestätigt. Unbedingt im Spam-Ordner nach dem Code schauen.']);
            setIsRetryAttempt(true);
            setUberDichSubmitted(true);
            setTelephoneSubmitted(true);
          }
        }
      } else {
        setErrors([error.message]);
      }
    }
  };

  const handleBackToTelephone = () => {
    setTelephoneSubmitted(false);
    setIsUberDichDisplayed(false);
    setUberDichSubmitted(false);
    setIsCreatingAccount(false);
    setOtpSubmitError('');
    setResetSocialForm(true);
    setIsRetryAttempt(false);
    setEmailSubmitted(false);
  };

  const handleTelephoneLogin = () => {
    setErrors([])
    setOtpSubmitError('');
    setShowTelephoneInput(prevState => !prevState);
  };

  const handleOtpSubmit = async () => {
    // OTP is already confirmed in the OtpChecker component
    // Now complete the registration
    setIsVerifyingOtp(true);
    setOtpSubmitError('');

    try {
      // Sign in after successful OTP confirmation
      await signIn({
        username: email || `+49${telephone}`,
        password: password
      });

      const registerPayload = {
        email: email || `+49${telephone}`,
        password,
        attributes: {
          given_name: name,
          family_name: surname,
          birthDate: birthday,
          email: email,
          phone_number: telephone ? `+49${telephone}` : undefined
        }
      };

      // Update user attributes (GraphQL backend) and sync full profile into Redux
      const registerResult = await dispatch(registerUser(registerPayload));

      if (registerUser.rejected.match(registerResult) || registerResult?.type === registerUser.rejected.type) {
        throw new Error(registerResult.payload || registerResult.error?.message || 'Registrierung fehlgeschlagen');
      }

      await dispatch(checkAuthState());

      const redirectTarget = getAuthRedirectPath(location, normalizeRedirectPath(location));

      setIsVerifyingOtp(false);
      trackSignup({
        method: showTelephoneInput ? 'phone' : 'email',
        source: 'registration_modal',
      });

      // Close modal and redirect
      onClose();
      if (redirectTarget === '/') {
        navigate('/', { replace: true, state: { showWelcome: true } });
      } else {
        navigate(redirectTarget, { replace: true });
      }
    } catch (error) {
      setIsVerifyingOtp(false);
      setOtpSubmitError(error.message);
    }
  };

  return (
    <Modal open={open} onBackdropClick={handleBackdropClick}>
      {(telephoneSubmitted || isUberDichDisplayed) && (
        <div className={classes.backEmailContainer} onClick={handleBackToTelephone}>
          <img src={backArrow} alt='back to email' />
          <span>Zurück</span>
        </div>
      )}
      <div className={classes.modalContainer}>
        <button className={classes.closeButton} onClick={onClose}>
          <img src={closeButton} alt="close-button" />
        </button>
        <h1 className='titleSemibold'>
          {telephoneSubmitted ? 'E-Mail bestätigen' : (isUberDichDisplayed ? 'Über dich' : 'Registrieren')}
        </h1>
        {!isUberDichDisplayed && !telephoneSubmitted && (
          <form className={classes.form} onSubmit={handleSubmit} noValidate>
            {showTelephoneInput ? (
              <div className={classes.phoneInputContainer} style={{ marginBottom: errors.length > 0 ? '14.5px' : '29.88px' }}>
                <div className={classes.countryFlagContainer}>
                  <img src={GermanFlag} alt='Germany Country Telephone Code' />
                </div>
                <span className={classes.countryCode}>+49</span>
                <div className={classes.smallLine}></div>
                <input
                  className='slimRegularLarge'
                  style={{ top : telephone ? "27.49px" : "" }}
                  type="number"
                  value={telephone}
                  onChange={(e) => setTelephone(e.target.value)}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                />
                <label className={`${classes.phoneLabel} ${isFocused ? classes.phoneFocused : ''}`}>
                  Telefonnummer eingeben
                </label>
              </div>
            ) : (
              <>
                <div className={classes.inputContainer} style={{ marginBottom: errors.length > 0 ? '14.5px' : '30.01px' }}>
                  <label className={`${classes.label} ${isFocused || email ? classes.focused : ''}`}>
                    E-Mail eingeben
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                  />
                </div>
                {emailSubmitted && (
                  <div className={classes.inputContainer} style={{ marginBottom: errors.length > 0 ? '14.5px' : '30.01px', position: 'relative' }}>
                    <label className={`${classes.label} ${isFocused || password ? classes.focused : ''}`}>
                      Passwort eingeben
                    </label>
                    <input
                      className={classes.passwordInput}
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        updatePasswordErrors(e.target.value);
                      }}
                      onFocus={() => {
                        handleFocus();
                        setPasswordTouched(true);
                      }}
                      onBlur={handleBlur}
                    />
                    <button
                      type="button"
                      className={`${classes.showPasswordButton} regularXs`}
                      onClick={() => setShowPassword(prev => !prev)}
                    >
                      {showPassword ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="18" viewBox="0 0 28 18" fill="none">
                          <path d="M14.0026 13.9955C11.0178 13.9955 8.59007 11.5677 8.59007 8.58293C8.59007 5.59818 11.0178 3.1704 14.0026 3.1704C16.9873 3.1704 19.4151 5.59818 19.4151 8.58293C19.4151 11.5677 16.9873 13.9955 14.0026 13.9955ZM14.0026 4.59851C11.8033 4.59851 10.0182 6.38364 10.0182 8.58293C10.0182 10.7822 11.8033 12.5673 14.0026 12.5673C16.2019 12.5673 17.987 10.7822 17.987 8.58293C17.987 6.38364 16.2019 4.59851 14.0026 4.59851Z" fill="#696F76"/>
                          <path d="M14.0383 17.1659C13.4099 17.1587 12.8458 17.1159 12.2317 17.0302C5.56962 16.1162 1.63518 11.0678 0.257059 8.93281L0 8.54008L0.257059 8.14735C1.64232 6.04803 5.5839 1.04252 12.1818 0.13567C12.8101 0.0499838 13.3956 0.00714054 13.9669 0C14.5953 0.00714054 15.1594 0.0499838 15.7734 0.13567C22.4427 1.0568 26.37 6.1123 27.7481 8.24018L27.998 8.63291L27.741 9.0185C26.3557 11.125 22.4141 16.1233 15.8163 17.0302C15.1879 17.1159 14.6024 17.1587 14.0312 17.1659H14.0383ZM1.71373 8.54722C3.18468 10.6822 6.72639 14.8309 12.4245 15.6164C12.9744 15.6949 13.4885 15.7306 13.9883 15.7377C14.5381 15.7377 15.0665 15.6878 15.6306 15.6164C21.2717 14.838 24.8134 10.7322 26.2915 8.62577C24.8276 6.49075 21.2859 2.3421 15.5807 1.5495C15.0308 1.47095 14.5167 1.43525 14.0169 1.42811C13.4671 1.42811 12.9387 1.47809 12.3746 1.5495C6.72639 2.32782 3.19182 6.43362 1.71373 8.54722Z" fill="#696F76"/>
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="24" viewBox="0 0 28 24" fill="none">
                          <path d="M17.986 11.11C17.986 11.6884 17.8646 12.2453 17.6361 12.738L12.3738 7.47572C12.8665 7.24723 13.4234 7.12585 14.0018 7.12585C16.201 7.12585 17.986 8.91088 17.986 11.11Z" fill="#696F76"/>
                          <path d="M28.0036 11.1529L27.7466 11.5456C26.8755 12.8665 25.0047 15.3299 22.0916 17.2006L18.6929 13.7947C19.157 13.0022 19.414 12.0882 19.414 11.11C19.414 8.12547 16.9864 5.69783 14.0018 5.69783C13.0236 5.69783 12.1097 5.95487 11.3171 6.41898L8.5396 3.64147C9.64632 3.19164 10.8601 2.84177 12.1811 2.66327C12.788 2.57759 13.3877 2.53475 13.9661 2.52761C14.6016 2.53475 15.1728 2.57759 15.7726 2.66327C22.4414 3.58435 26.3685 8.63242 27.7466 10.7673L28.0036 11.1529Z" fill="#696F76"/>
                          <path d="M2.94173 0L2.92745 0.0142803L1.92784 1.00676L5.93345 5.01237C5.93345 5.01237 5.91917 5.01951 5.91203 5.02665C2.99886 6.89023 1.12814 9.36071 0.257045 10.6816L0 11.0743L0.257045 11.4671C1.63509 13.5948 5.57644 18.6429 12.231 19.5568C12.8308 19.6425 13.402 19.6853 14.0375 19.6925C14.6087 19.6853 15.2085 19.6425 15.8225 19.5568C17.1435 19.3783 18.3573 19.0356 19.464 18.5786C19.464 18.5786 19.4783 18.5715 19.4854 18.5643L24.0265 23.1055L25.019 22.1058L25.0333 22.0916L2.94173 0ZM14.0018 16.5223C11.0172 16.5223 8.58958 14.0946 8.58958 11.11C8.58958 10.1318 8.84662 9.21791 9.31073 8.42536L10.3675 9.4821C10.139 9.97476 10.0176 10.5317 10.0176 11.11C10.0176 13.3092 11.8026 15.0942 14.0018 15.0942C14.5802 15.0942 15.1371 14.9729 15.6297 14.7444L16.6865 15.8011C15.8939 16.2652 14.98 16.5223 14.0018 16.5223Z" fill="#696F76"/>
                        </svg>
                      )}
                    </button>
                  </div>
                )}
              </>
            )}
            {errors.length > 0 && (
              <>
                {errors.map((error, index) => (
                  <div key={index} className={classes.error}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M1.02734 1.40723L10.9725 11.3524" stroke="#CB1541" strokeWidth="1.5" strokeMiterlimit="10"/>
                      <path fillRule="evenodd" clipRule="evenodd" d="M4.9452 6.37988L0.5 1.93469L1.5548 0.879883L6 5.32508L10.4452 0.879883L11.5 1.93469L7.0548 6.37988L11.5 10.8251L10.4452 11.8799L6 7.43469L1.5548 11.8799L0.5 10.8251L4.9452 6.37988Z" fill="#CB1541"/>
                    </svg>
                    <span>{error}</span>
                  </div>
                ))}
              </>
            )}
            <button style={{ marginBottom: emailSubmitted ? '20px' : '30px' }} className={`${classes.submitButton} mediumLarge`} type="submit">Weiter</button>
          </form>
        )}
        {isUberDichDisplayed && !telephoneSubmitted && (
          <UberDich
            email={email}
            telephone={telephone}
            password={password}
            onUberDichSubmit={handleUberDichSubmit}
            isCreating={isCreatingAccount}
          />
        )}
        {telephoneSubmitted && (
          <OtpChecker
            telephone={telephone}
            email={email}
            onOtpSubmit={handleOtpSubmit}
            onUberDichDisplay={() => {}}
            isRetryAttempt={isRetryAttempt}
            isVerifying={isVerifyingOtp}
            submitError={otpSubmitError}
            onClearSubmitError={() => setOtpSubmitError('')}
          />
        )}
        {!isUberDichDisplayed && !telephoneSubmitted && (
          <>
            <div className={classes.oderContainer}>
              <div></div>
              <span>oder</span>
              <div></div>
            </div>
            <SocialForm onTelephoneLogin={handleTelephoneLogin} resetFirstBoxClicked={resetSocialForm} authIntent="signup" />
            <div className={classes.registerContainer}>
              <span>Hast du schon ein Konto?</span>
              <button onClick={onSwitchToSignIn}>Hier anmelden</button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default Register;
